import {
  TextField,
  Grid,
  MenuItem,
  Box,
  FormControlLabel,
  Checkbox,
  Link,
  Autocomplete,
  CircularProgress,
  InputLabel,
  Select,
  Chip,
  ListItemText,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import { getControlId } from '@veeva-ctv/utils';
import { FormikProps } from 'formik';
import { useLazyQuery } from '@apollo/client';
import { useStyles as useStylesAutocomplete } from '@/hooks/styles/autocomplete';
import { GENERAL_SEARCH } from '@/queries';
import { SearchRounded } from '@mui/icons-material';
import { colors } from '@veeva-ctv/component-lib';
import useURLsContext from '@/context/URLsContext';
import { UIOnlyFields } from '@/types';
import { REGULAR_FLOW_SCREEN_NAMES } from '@/constants/eventTrackingConstants';
import { useEffect } from 'react';
import { push } from '@/utils/matomoUtils';
import {
  SiteApplicationInput,
  SearchType,
  GeneralSearchQuery,
  GeneralSearchQueryVariables,
} from '@/gql/graphql';
import { SITE_CONNECT_FROM_TRACKER } from '@/constants';
import { debounce } from '@/utils';
import { useCallback } from 'react';

interface SiteDetailsFormProps {
  formik: FormikProps<UIOnlyFields & SiteApplicationInput>;
  fromSiteConnect: boolean;
}

const studiesPerYearOptions = ['1-10', '11-20', '21-50', '51-100', '100+'];
const productsOfInterestOptions = ['CTMS', 'eConsent', 'eISF'];

const SiteDetailsForm = ({ formik, fromSiteConnect }: SiteDetailsFormProps) => {
  const classesAutocomplete = useStylesAutocomplete();
  const urls = useURLsContext();
  const [searchSponsors, { data: searchSponsorsData, loading: searchSponsorsLoading }] =
    useLazyQuery<GeneralSearchQuery, GeneralSearchQueryVariables>(GENERAL_SEARCH);

  useEffect(() => {
    push(['trackPageView', REGULAR_FLOW_SCREEN_NAMES.submit]);
  }, []);

  if (fromSiteConnect && !formik.values.marketingSource) {
    void formik.setFieldValue('marketingSource', SITE_CONNECT_FROM_TRACKER);
  }

  const doSponsorSearch = useCallback(
    debounce((value: string) => {
      void searchSponsors({
        variables: {
          input: {
            generalSearchText: value.replace(/[\n\t]/g, '').trim(),
            filterText: '',
            generalSearchType: SearchType.Sponsor,
          },
          size: 10,
        },
      });
    }, 300),
    []
  );
  return (
    <Grid container rowSpacing={1}>
      {!fromSiteConnect && (
        <Grid item xs={12}>
          <TextField
            id={getControlId({ type: 'text', name: 'marketingSource' })}
            name="marketingSource"
            label="How did you hear about Veeva SiteVault?*"
            fullWidth
            inputProps={{ autoComplete: 'one-time-code' }}
            value={formik.values.marketingSource}
            error={formik.touched.marketingSource && Boolean(formik.errors.marketingSource)}
            helperText={formik.touched.marketingSource && formik.errors.marketingSource}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField
          id={getControlId({
            type: 'select',
            name: 'asCRO',
          })}
          name="asCRO"
          fullWidth
          select
          label="Are you signing up at the request of a Sponsor/CRO?*"
          value={formik.values.asCRO}
          onBlur={formik.handleBlur}
          onChange={(e) => void formik.setFieldValue('asCRO', e.target.value === 'true')}>
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </TextField>
      </Grid>

      {formik.values.asCRO && (
        <Grid item xs={12}>
          <Box mt={1}>
            <Autocomplete
              autoComplete
              id={getControlId({ type: 'text', name: 'sponsor' })}
              loading={searchSponsorsLoading}
              inputValue={formik.values.referralSource as string}
              freeSolo
              onInputChange={(_e, value, reason) => {
                if (value === '' || reason === 'clear') {
                  void formik.setFieldValue('referralSource', '');
                  doSponsorSearch('');
                  return;
                }

                doSponsorSearch(value);
                void formik.setFieldValue('referralSource', value);
              }}
              onChange={(_e, value) => {
                void formik.setFieldValue('referralSource', value || '');
              }}
              options={searchSponsorsData?.generalSearch.payload || []}
              filterOptions={(options) => options}
              isOptionEqualToValue={(option, value) => {
                return option === value;
              }}
              classes={{
                option: classesAutocomplete.optionSearch,
                listbox: classesAutocomplete.listbox,
              }}
              popupIcon={<SearchRounded />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="referralSource"
                  margin="dense"
                  label="What Sponsor/CRO?*"
                  onBlur={formik.handleBlur}
                  error={formik.touched.referralSource && Boolean(formik.errors.referralSource)}
                  helperText={formik.touched.referralSource && formik.errors.referralSource}
                  inputProps={{
                    ...params.inputProps,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    autoComplete: 'one-time-code', // disable autocomplete and autofill
                    endAdornment: (
                      <>
                        {searchSponsorsLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : (
                          !formik.values.referralSource && (
                            <SearchRounded
                              sx={(theme) => ({
                                color: colors.grey[800],
                                marginRight: theme.spacing(0.5),
                                marginLeft: theme.spacing(0.5),
                              })}
                            />
                          )
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField
          id={getControlId({ type: 'text', name: 'studiesPerYear' })}
          name="studiesPerYear"
          label="How many studies are active at your site in a typical year?*"
          fullWidth
          select
          value={formik.values.studiesPerYear}
          error={formik.touched.studiesPerYear && Boolean(formik.errors.studiesPerYear)}
          helperText={formik.touched.studiesPerYear && formik.errors.studiesPerYear}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}>
          {studiesPerYearOptions.map((elem, idx) => (
            <MenuItem key={idx} value={elem}>
              {elem}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12}>
        <InputLabel
          id={getControlId({ type: 'text', name: 'productsOfInterestLabel' })}
          sx={{
            color:
              formik.touched.productsOfInterest && Boolean(formik.errors.productsOfInterest)
                ? 'error.main'
                : '',
          }}>
          What products are you interested in?*
        </InputLabel>
        <Select
          labelId={getControlId({ type: 'text', name: 'productsOfInterestLabel' })}
          id={getControlId({ type: 'text', name: 'productsOfInterest' })}
          fullWidth
          multiple
          sx={{
            '& .MuiOutlinedInput-input': {
              padding: '6.5px 14px',
              margin: 0,
              height: 24,
            },
          }}
          name="productsOfInterest"
          value={formik.values.productsOfInterest}
          error={formik.touched.productsOfInterest && Boolean(formik.errors.productsOfInterest)}
          onChange={(e) => {
            const values = e.target.value as [string];
            void formik.setFieldValue('productsOfInterest', values.sort())
          }}
          onBlur={formik.handleBlur}
          input={
            <OutlinedInput id="select-multiple-products-of-interest" label="Products of interest" />
          }
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected?.map((value) => (
                <Chip
                  size={'small'}
                  key={value}
                  label={value}
                />
              ))}
            </Box>
          )}>
          {productsOfInterestOptions.map((product) => (
            <MenuItem key={product} value={product}>
              <Checkbox checked={formik.values.productsOfInterest?.includes(product)} />
              <ListItemText primary={product} />
            </MenuItem>
          ))}
        </Select>
        {formik.touched.productsOfInterest && Boolean(formik.errors.productsOfInterest) && (
          <FormHelperText sx={{ color: 'error.main' }}>
            {formik.errors.productsOfInterest}
          </FormHelperText>
        )}
      </Grid>

      <Grid item xs={12}>
        <Box mt={-1} mb={1} ml={0.2}>
          <FormControlLabel
            control={
              <Checkbox
                id={getControlId({ type: 'checkbox', name: 'agreedToTOS' })}
                name="agreedToTOS"
                checked={formik.values.agreedToTOS}
                onChange={(e) =>
                  void formik.setFieldValue('agreedToTOS', e.target.checked)
                }></Checkbox>
            }
            label={
              <>
                I agree to the Veeva SiteVault{' '}
                <Link
                  href={urls?.TOSUrl ? urls.TOSUrl : process.env.NEXT_PUBLIC_TERMS_EXTERNAL_URL}
                  color="primary"
                  target="_blank">
                  Terms of Service
                </Link>
                .*
              </>
            }
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box mt={-1} mb={1} ml={0.2}>
          <FormControlLabel
            control={
              <Checkbox
                id={getControlId({ type: 'checkbox', name: 'emailOptIn' })}
                name="emailOptIn"
                checked={Boolean(formik.values.emailOptIn)}
                onChange={(e) =>
                  void formik.setFieldValue('emailOptIn', e.target.checked)
                }></Checkbox>
            }
            label="Yes - I agree to receive notifications from Veeva."
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default SiteDetailsForm;
